import type { User } from "@sendbird/chat";
import type { GroupChannel } from "@sendbird/chat/groupChannel";

export const SUPPORTED_TEEN_LANGUAGES = {
  es: { english: "Spanish", native: "Español" },
  "zh-CN": { english: "Chinese (Simplified)", native: "中文 (简体)" },
  tl: { english: "Tagalog", native: "Tagalog" },
  vi: { english: "Vietnamese", native: "Tiếng Việt" },
  ar: { english: "Arabic", native: "العربية" },
  fr: { english: "French", native: "Français" },
  ko: { english: "Korean", native: "한국어" },
  ru: { english: "Russian", native: "Русский" },
  ht: { english: "Haitian Creole", native: "Kreyòl Ayisyen" },
  pt: { english: "Portuguese", native: "Português" },
} as const;

export type SupportedTeenLanguage = keyof typeof SUPPORTED_TEEN_LANGUAGES;

export const MENTOR_LANGUAGE = "en";

export const PREFERRED_LANGUAGE_META_KEY = "preferredLanguage";

const isSupportedTeenLanguage = (
  value: string | undefined | null,
): value is SupportedTeenLanguage =>
  !!value && value in SUPPORTED_TEEN_LANGUAGES;

export const getTeenLanguageFromUser = (
  user: { metaData?: object } | null | undefined,
): SupportedTeenLanguage | null => {
  const meta = user?.metaData as Record<string, string> | undefined;
  const value = meta?.[PREFERRED_LANGUAGE_META_KEY];
  return isSupportedTeenLanguage(value) ? value : null;
};

export const getTeenLanguageFromChannel = (
  channel: GroupChannel | null | undefined,
  currentUserId: string | undefined,
): SupportedTeenLanguage | null => {
  if (!channel || !currentUserId) return null;
  const otherMember = channel.members.find(
    (member) => member.userId !== currentUserId,
  );
  return getTeenLanguageFromUser(otherMember as User | undefined);
};

export const getDisplayText = (
  message: { message: string; translations?: Record<string, string> | object },
  viewerLanguage: string,
): { text: string; isTranslated: boolean } => {
  const translations = message.translations as
    | Record<string, string>
    | undefined;
  const translated = translations?.[viewerLanguage];
  if (translated && translated !== message.message) {
    return { text: translated, isTranslated: true };
  }
  return { text: message.message, isTranslated: false };
};
